/**
 * Read-only summary of a linked electronic module's current spec, rendered from
 * the ref's auto-synced `electronics.*` cache and resolved through the relevant
 * lookup collections for human-friendly display names. This is a display-only
 * mirror of the module entity — editing happens on the module itself, not here.
 *
 * Note: `sensors` and `generation` are module-doc fields that the
 * `ElectronicsInfo` cache does not currently carry, so they are not shown here.
 */
import { useEffect, useMemo, useState } from 'react';
import { getLookupCollection } from '../../services/firebase';
import type { ElectronicsInfo, LookupValue } from '../../types';

interface FieldSpec {
  /** Key on the `electronics` cache object. */
  key: keyof ElectronicsInfo;
  label: string;
  /** Lookup collection used to resolve the slug(s) to display names. */
  lookup?: string;
  /** Multi-value field (renders as chips). */
  multi?: boolean;
}

/** Single-value spec fields, in display order. */
const SCALAR_FIELDS: FieldSpec[] = [
  { key: 'display', label: 'Display type', lookup: 'lookup_display_type' },
  { key: 'lcdType', label: 'LCD type', lookup: 'lookup_lcd_type' },
  { key: 'lightType', label: 'Light type', lookup: 'lookup_light_type' },
  { key: 'battery', label: 'Battery', lookup: 'lookup_battery_type' },
  { key: 'batteryLife', label: 'Battery life' },
  { key: 'chargeTime', label: 'Charge time' },
  { key: 'displayLayout', label: 'Display layout', lookup: 'lookup_display_layout' },
  { key: 'viewingAngle', label: 'Viewing angle', lookup: 'lookup_viewing_angle' },
  { key: 'dstSupport', label: 'DST support', lookup: 'lookup_dst_support' },
  { key: 'calendarType', label: 'Calendar type', lookup: 'lookup_calendar_type' },
  {
    key: 'stopwatchPrecision',
    label: 'Stopwatch precision',
    lookup: 'lookup_stopwatch_precision',
  },
  { key: 'powerSource', label: 'Power source', lookup: 'lookup_power_source' },
  { key: 'syncSource', label: 'Sync source', lookup: 'lookup_sync_source' },
];

/** Multi-value spec fields, in display order. */
const MULTI_FIELDS: FieldSpec[] = [
  {
    key: 'timeSystems',
    label: 'Time systems',
    lookup: 'lookup_time_systems',
    multi: true,
  },
  { key: 'alarmTypes', label: 'Alarm types', lookup: 'lookup_alarm_types', multi: true },
];

/** slug (lowercased) → displayName, per lookup collection. */
type LookupMaps = Record<string, Record<string, string>>;

function buildMap(values: LookupValue[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const lv of values) {
    map[lv.value.toLowerCase()] = lv.displayName || lv.value;
  }
  return map;
}

function resolve(maps: LookupMaps, lookup: string | undefined, slug: string): string {
  const trimmed = slug.trim();
  if (!lookup) return trimmed;
  return maps[lookup]?.[trimmed.toLowerCase()] || trimmed;
}

interface ModuleDetailsSummaryProps {
  electronics: ElectronicsInfo | undefined;
}

export function ModuleDetailsSummary({ electronics }: ModuleDetailsSummaryProps) {
  const [maps, setMaps] = useState<LookupMaps>({});

  // Fields with a present value, split scalar vs multi.
  const scalarRows = useMemo(() => {
    if (!electronics) return [];
    return SCALAR_FIELDS.map((f) => ({
      spec: f,
      value: (electronics[f.key] as string | undefined)?.trim() || '',
    })).filter((r) => r.value.length > 0);
  }, [electronics]);

  const multiRows = useMemo(() => {
    if (!electronics) return [];
    return MULTI_FIELDS.map((f) => ({
      spec: f,
      values: ((electronics[f.key] as string[] | undefined) || [])
        .map((s) => s.trim())
        .filter(Boolean),
    })).filter((r) => r.values.length > 0);
  }, [electronics]);

  const hasContent = scalarRows.length > 0 || multiRows.length > 0;

  // Load only the lookup collections referenced by present values.
  useEffect(() => {
    const needed = new Set<string>();
    for (const r of scalarRows) if (r.spec.lookup) needed.add(r.spec.lookup);
    for (const r of multiRows) if (r.spec.lookup) needed.add(r.spec.lookup);
    const missing = [...needed].filter((c) => !(c in maps));
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        missing.map(async (c) => {
          try {
            return [c, buildMap(await getLookupCollection(c))] as const;
          } catch {
            return [c, {}] as const;
          }
        }),
      );
      if (cancelled) return;
      setMaps((prev) => {
        const next = { ...prev };
        for (const [c, m] of entries) next[c] = m;
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [scalarRows, multiRows, maps]);

  if (!hasContent) return null;

  return (
    <div className="border-t border-gray-100 pt-3">
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        Module details{' '}
        <span className="font-normal normal-case text-gray-400">(read-only)</span>
      </h4>
      <div className="space-y-1.5">
        {scalarRows.map(({ spec, value }) => (
          <div key={String(spec.key)} className="flex items-start gap-2 text-sm py-0.5">
            <span className="w-36 shrink-0 text-gray-500">{spec.label}</span>
            <span className="flex-1 break-words text-gray-800">
              {resolve(maps, spec.lookup, value)}
            </span>
          </div>
        ))}
        {multiRows.map(({ spec, values }) => (
          <div key={String(spec.key)} className="flex items-start gap-2 text-sm py-0.5">
            <span className="w-36 shrink-0 text-gray-500">{spec.label}</span>
            <span className="flex flex-1 flex-wrap gap-1">
              {values.map((slug) => (
                <span
                  key={slug}
                  className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700"
                >
                  {resolve(maps, spec.lookup, slug)}
                </span>
              ))}
            </span>
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs text-gray-400">
        Auto-synced from the linked module. Edit values on the module entity.
      </p>
    </div>
  );
}
